/**
 * Cookie-based authentication for cross-domain, persistent login
 *
 * Shares the qixit_auth_* cookie with products-web checkout.
 * Session: 12 hours after last activity or end of local calendar day.
 */

interface AuthData {
  userId: string;
  username: string;
  token: string;
  loginTime: number;
  lastActivityAt?: number;
  expiresAt: number;
}

const AUTH_COOKIE_BASE_NAME = 'qixit_auth';
const LEGACY_AUTH_COOKIE_NAME = AUTH_COOKIE_BASE_NAME;

type AuthEnvironment = 'dev' | 'stage' | 'prod';

function getAuthEnvironment(): AuthEnvironment {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return 'dev';
  }

  if (
    hostname.startsWith('st') ||
    hostname.includes('-stage') ||
    hostname.includes('.stage.')
  ) {
    return 'stage';
  }

  return 'prod';
}

const AUTH_COOKIE_NAME = `${AUTH_COOKIE_BASE_NAME}_${getAuthEnvironment()}`;

function getAuthCookieCandidates(): string[] {
  return Array.from(new Set([AUTH_COOKIE_NAME, LEGACY_AUTH_COOKIE_NAME]));
}

const SESSION_IDLE_MS = 12 * 60 * 60 * 1000;

function endOfLocalDayMs(fromMs: number): number {
  const d = new Date(fromMs);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function computeSessionExpiresAt(activityMs: number): number {
  return Math.min(activityMs + SESSION_IDLE_MS, endOfLocalDayMs(activityMs));
}

function getCookieDomain(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }

  if (hostname.includes('qixit.com')) {
    return '.qixit.com';
  }

  return '';
}

function setCookie(name: string, value: string, expiresMs: number): void {
  const expires = new Date(Date.now() + expiresMs).toUTCString();
  const domain = getCookieDomain();

  let cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;

  if (domain) {
    cookie += `; domain=${domain}`;
  }

  if (window.location.protocol === 'https:') {
    cookie += '; Secure; SameSite=None';
  } else {
    cookie += '; SameSite=Lax';
  }

  document.cookie = cookie;
}

function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue.join('='));
    }
  }
  return null;
}

function deleteCookie(name: string): void {
  const domain = getCookieDomain();
  const isSecure = window.location.protocol === 'https:';

  let cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  if (domain) {
    cookie += `; domain=${domain}`;
  }
  if (isSecure) {
    cookie += '; Secure; SameSite=None';
  } else {
    cookie += '; SameSite=Lax';
  }
  document.cookie = cookie;

  let cookieNoDomain = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  if (isSecure) {
    cookieNoDomain += '; Secure; SameSite=None';
  }
  document.cookie = cookieNoDomain;

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

export function setSharedAuthCookie(userId: string, username: string, token: string): void {
  const now = Date.now();
  const expiresAt = computeSessionExpiresAt(now);

  const authData: AuthData = {
    userId,
    username,
    token,
    loginTime: now,
    lastActivityAt: now,
    expiresAt
  };

  setCookie(AUTH_COOKIE_NAME, JSON.stringify(authData), Math.max(0, expiresAt - now));
  if (AUTH_COOKIE_NAME !== LEGACY_AUTH_COOKIE_NAME) {
    deleteCookie(LEGACY_AUTH_COOKIE_NAME);
  }
  console.log('🔐 Shared auth cookie set for:', username);
}

export function isMicropayScopedToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return false;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const json = JSON.parse(atob(padded)) as { login_scope?: string };
    return String(json.login_scope || '').toLowerCase() === 'micropay';
  } catch {
    return false;
  }
}

export function getSharedAuthFromCookie(): AuthData | null {
  for (const cookieName of getAuthCookieCandidates()) {
    const cookieValue = getCookie(cookieName);
    if (!cookieValue) {
      continue;
    }

    try {
      const authData: AuthData = JSON.parse(cookieValue);
      const lastActivityAt = authData.lastActivityAt ?? authData.loginTime;
      const expiresAt = authData.expiresAt ?? computeSessionExpiresAt(lastActivityAt);

      if (Date.now() > expiresAt) {
        console.log('🍪 Shared auth cookie expired');
        clearSharedAuthCookie();
        return null;
      }

      if (isMicropayScopedToken(authData.token)) {
        return null;
      }

      if (cookieName === LEGACY_AUTH_COOKIE_NAME && AUTH_COOKIE_NAME !== LEGACY_AUTH_COOKIE_NAME) {
        setCookie(AUTH_COOKIE_NAME, JSON.stringify({ ...authData, lastActivityAt, expiresAt }), Math.max(0, expiresAt - Date.now()));
        deleteCookie(LEGACY_AUTH_COOKIE_NAME);
      }

      return { ...authData, lastActivityAt, expiresAt };
    } catch (e) {
      console.error('Failed to parse shared auth cookie:', e);
      return null;
    }
  }

  return null;
}

export function clearSharedAuthCookie(): void {
  for (const cookieName of getAuthCookieCandidates()) {
    deleteCookie(cookieName);
  }
  const logoutCookie = `qixit_logout_at_${getAuthEnvironment()}`;
  setCookie(logoutCookie, String(Date.now()), SESSION_IDLE_MS);
  console.log('🔐 Shared auth cookie cleared');
}

export function isSharedAuthValid(): { authenticated: boolean; userId: string; username: string; token: string } {
  const authData = getSharedAuthFromCookie();

  if (authData) {
    return {
      authenticated: true,
      userId: authData.userId,
      username: authData.username,
      token: authData.token
    };
  }

  return {
    authenticated: false,
    userId: '',
    username: '',
    token: ''
  };
}
